import { useParams, Navigate } from 'react-router-dom';
import {
  Thermometer,
  Droplets,
  Zap,
  Wind,
  Snowflake,
  Fan,
  Flame,
  ShowerHead,
  Home,
  type LucideIcon,
} from 'lucide-react';
import type { ServiceType } from '@/types';
import {
  ServiceHero,
  ServiceGrid,
  WhyChooseSection,
  ProblemsSection,
  TestimonialCard,
  FAQSection,
  ServiceCTA,
  FeaturedProviders,
  OtherServicesGrid,
} from '@/components/services';

// Service type configuration with icons and colors
const SERVICE_CONFIG: Record<
  ServiceType,
  {
    icon: LucideIcon;
    color: string;
    bgColor: string;
    lightBg: string;
    gradient: string;
  }
> = {
  hvac: {
    icon: Thermometer,
    color: 'text-blue-600',
    bgColor: 'bg-blue-600',
    lightBg: 'bg-blue-50',
    gradient: 'from-blue-600 via-blue-700 to-blue-900',
  },
  plumbing: {
    icon: Droplets,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-600',
    lightBg: 'bg-cyan-50',
    gradient: 'from-cyan-600 via-cyan-700 to-cyan-900',
  },
  electrical: {
    icon: Zap,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-500',
    lightBg: 'bg-yellow-50',
    gradient: 'from-yellow-500 via-yellow-600 to-yellow-800',
  },
  heating: {
    icon: Flame,
    color: 'text-orange-600',
    bgColor: 'bg-orange-600',
    lightBg: 'bg-orange-50',
    gradient: 'from-orange-500 via-orange-600 to-orange-800',
  },
  cooling: {
    icon: Snowflake,
    color: 'text-sky-600',
    bgColor: 'bg-sky-600',
    lightBg: 'bg-sky-50',
    gradient: 'from-sky-500 via-sky-600 to-sky-800',
  },
  ventilation: {
    icon: Fan,
    color: 'text-teal-600',
    bgColor: 'bg-teal-600',
    lightBg: 'bg-teal-50',
    gradient: 'from-teal-500 via-teal-600 to-teal-800',
  },
  refrigeration: {
    icon: Snowflake,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-600',
    lightBg: 'bg-indigo-50',
    gradient: 'from-indigo-500 via-indigo-600 to-indigo-800',
  },
  water_heater: {
    icon: ShowerHead,
    color: 'text-red-600',
    bgColor: 'bg-red-600',
    lightBg: 'bg-red-50',
    gradient: 'from-red-500 via-red-600 to-red-800',
  },
  duct_cleaning: {
    icon: Wind,
    color: 'text-gray-600',
    bgColor: 'bg-gray-600',
    lightBg: 'bg-gray-100',
    gradient: 'from-gray-500 via-gray-600 to-gray-800',
  },
  insulation: {
    icon: Home,
    color: 'text-green-600',
    bgColor: 'bg-green-600',
    lightBg: 'bg-green-50',
    gradient: 'from-green-500 via-green-600 to-green-800',
  },
};

// Valid service type slugs
const VALID_SERVICE_SLUGS = Object.keys(SERVICE_CONFIG);

export function ServicePage() {
  const { serviceType } = useParams<{ serviceType: string }>();

  // Validate service type
  if (!serviceType || !VALID_SERVICE_SLUGS.includes(serviceType)) {
    return <Navigate to="/" replace />;
  }

  const service = serviceType as ServiceType;
  const config = SERVICE_CONFIG[service];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <ServiceHero
        serviceType={service}
        icon={config.icon}
        gradient={config.gradient}
      />

      {/* Services Offered */}
      <ServiceGrid
        serviceType={service}
        bgColor={config.lightBg}
        iconBgColor={config.bgColor}
      />

      {/* Why Choose Our Pros */}
      <WhyChooseSection
        serviceType={service}
        color={config.color}
        lightBg={config.lightBg}
      />

      {/* Featured Providers */}
      <FeaturedProviders
        serviceType={service}
        color={config.color}
        bgColor={config.bgColor}
      />

      {/* Common Problems */}
      <ProblemsSection
        serviceType={service}
        color={config.color}
      />

      {/* Testimonial */}
      <TestimonialCard
        serviceType={service}
        color={config.color}
        bgColor={config.bgColor}
      />

      {/* FAQ */}
      <FAQSection serviceType={service} />

      {/* CTA */}
      <ServiceCTA
        serviceType={service}
        gradient={config.gradient}
      />

      {/* Other Services */}
      <OtherServicesGrid excludeService={service} />
    </div>
  );
}
